import { createSignal, onMount, For, Show } from 'solid-js';
import { gameStore } from '@/stores/gameStore';
import { apiService } from '@/services/api';
import { useTranslation } from '@/i18n';
import './AdminDashboard.css';

interface Game {
  id: number;
  name: string;
  location: string;
  status: 'planned' | 'active' | 'finished';
  actual_start_time: string | null;
  duration_minutes: number;
  teams_count?: number;
  completed_teams?: number;
}

interface Team {
  id: number;
  name: string;
  phone_number: string;
  is_active: boolean;
  completion_time: string | null;
  final_game_time: string | null;
  game: number;
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [games, setGames] = createSignal<Game[]>([]);
  const [teams, setTeams] = createSignal<Team[]>([]);
  const [selectedGame, setSelectedGame] = createSignal<Game | null>(null);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<string | null>(null);

  const loadGames = async () => {
    try {
      const games = await apiService.getAdminGames();
      setGames(games);
    } catch (err) {
      setError('Failed to load games');
      console.error('Error loading games:', err);
    }
  };

  const loadTeams = async (gameId?: number) => {
    try {
      const teams = await apiService.getAdminTeams(gameId);
      setTeams(teams);
    } catch (err) {
      setError('Failed to load teams');
      console.error('Error loading teams:', err);
    }
  };

  const startGame = async (gameId: number) => {
    try {
      await apiService.startGame(gameId);
      gameStore.setNotification({
        type: 'success',
        message: t('admin.gameStarted')
      });
      await loadGames();
    } catch (err) {
      gameStore.setNotification({
        type: 'error',
        message: t('errors.serverError')
      });
    }
  };

  const endGame = async (gameId: number) => {
    try {
      await apiService.endGame(gameId);
      gameStore.setNotification({
        type: 'success',
        message: t('admin.gameEnded')
      });
      await loadGames();
    } catch (err) {
      gameStore.setNotification({
        type: 'error',
        message: t('errors.serverError')
      });
    }
  };

  const selectGame = (game: Game) => {
    setSelectedGame(game);
    loadTeams(game.id);
  };

  onMount(async () => {
    try {
      await loadGames();
      await loadTeams();
    } finally {
      setLoading(false);
    }
  });

  const formatTime = (timeString: string | null) => {
    if (!timeString) return 'Not started';
    return new Date(timeString).toLocaleString();
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      planned: 'status-planned',
      active: 'status-active',
      finished: 'status-finished'
    };
    return `status-badge ${statusClasses[status as keyof typeof statusClasses] || ''}`;
  };

  return (
    <div class="admin-dashboard">
      <div class="admin-header">
        <h1>{t('admin.title')}</h1>
        <button
          class="refresh-btn"
          onClick={() => {
            loadGames();
            loadTeams(selectedGame()?.id);
          }}
        >
          🔄 {t('admin.actions.refreshData')}
        </button>
      </div>

      <Show when={error()}>
        <div class="error-message">{error()}</div>
      </Show>

      <Show when={loading()}>
        <div class="loading">{t('common.loading')}</div>
      </Show>

      <Show when={!loading()}>
        <div class="admin-content">
          <div class="games-section">
            <h2>{t('admin.games')}</h2>
            <div class="games-grid">
              <For each={games()}>
                {(game) => (
                  <div 
                    class={`game-card ${selectedGame()?.id === game.id ? 'selected' : ''}`}
                    onClick={() => selectGame(game)}
                  >
                    <div class="game-header">
                      <h3>{game.name}</h3>
                      <span class={getStatusBadge(game.status)}>
                        {game.status.toUpperCase()}
                      </span>
                    </div>
                    <div class="game-details">
                      <p><strong>Location:</strong> {game.location}</p>
                      <p><strong>Duration:</strong> {game.duration_minutes} min</p>
                      <p><strong>Started:</strong> {formatTime(game.actual_start_time)}</p>
                      <Show when={game.teams_count !== undefined}>
                        <p><strong>Teams:</strong> {game.completed_teams || 0}/{game.teams_count || 0}</p>
                      </Show>
                    </div>
                    <div class="game-actions">
                      <Show when={game.status === 'planned'}>
                        <button
                          class="btn-start"
                          onClick={(e) => {
                            e.stopPropagation();
                            startGame(game.id);
                          }}
                        >
                          {t('admin.actions.startGame')}
                        </button>
                      </Show>
                      <Show when={game.status === 'active'}>
                        <button
                          class="btn-end"
                          onClick={(e) => {
                            e.stopPropagation();
                            endGame(game.id);
                          }}
                        >
                          {t('admin.actions.endGame')}
                        </button>
                      </Show>
                    </div>
                  </div>
                )}
              </For>
            </div>
          </div>

          <div class="teams-section">
            <h2>
              {t('admin.teams')}
              <Show when={selectedGame()}>
                <span class="section-subtitle">- {selectedGame()!.name}</span>
              </Show>
            </h2>
            <div class="teams-table">
              <Show when={teams().length === 0}>
                <div class="no-data">
                  {selectedGame() ? t('ranking.noTeams') : t('admin.selectGame')}
                </div>
              </Show>
              <Show when={teams().length > 0}>
                <table>
                  <thead>
                    <tr>
                      <th>{t('ranking.team')}</th>
                      <th>Phone</th>
                      <th>{t('common.status')}</th>
                      <th>{t('ranking.lastActivity')}</th>
                      <th>Final</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={teams()}>
                      {(team) => (
                        <tr class={team.is_active ? 'team-active' : 'team-inactive'}>
                          <td>{team.name}</td>
                          <td>{team.phone_number}</td>
                          <td>
                            <span class={`team-status ${team.is_active ? 'active' : 'inactive'}`}>
                              {team.is_active ? t('ranking.status.active') : t('ranking.status.inactive')}
                            </span>
                          </td>
                          <td>{formatTime(team.completion_time)}</td>
                          <td>{team.final_game_time || t('dashboard.gameActive')}</td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </Show>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
}